import type { DashboardRecipe } from '@/types/recipes';
import type { FolderResponseDto } from '@/types/folder.types';

/**
 * Walks up the parentId chain to build the full breadcrumb path
 * from root down to the given folder.
 */
export const buildFolderPath = (
  folderId: string | null | undefined,
  folders: FolderResponseDto[] | undefined,
): FolderResponseDto[] => {
  if (!folderId || !folders?.length) return [];

  const folder = folders.find((f) => f.id === folderId);
  if (!folder) return [];

  if (!folder.parentId) return [folder];

  return [...buildFolderPath(folder.parentId, folders), folder];
};

/**
 * Convert a timestamp (string or number) to milliseconds
 */
export const toMillis = (timestamp: string | number | undefined): number => {
  if (!timestamp) return 0;
  if (typeof timestamp === 'number') return timestamp;
  // Handle ISO date strings
  return new Date(timestamp).getTime();
};

const folderUpdatedSortMs = (folder: FolderResponseDto): number =>
  toMillis(folder.updatedAt) || toMillis(folder.createdAt);

const recipeUpdatedSortMs = (recipe: DashboardRecipe): number =>
  toMillis(recipe.updatedAt) || toMillis(recipe.createdAt);

/**
 * Get the latest updated timestamp from a folder, including all its nested content
 * @param folderId - The folder ID to check
 * @param allFolders - All folders in the system
 * @param allRecipes - All recipes in the system
 * @returns The latest timestamp in milliseconds
 */
export const getLatestUpdateInFolder = (
  folderId: string,
  allFolders: FolderResponseDto[],
  allRecipes: DashboardRecipe[],
): number => {
  const cache = new Map<string, number>();

  const calculateLatestUpdate = (id: string): number => {
    // Check cache first
    if (cache.has(id)) {
      return cache.get(id)!;
    }

    let latestUpdate = 0;

    // Get the folder itself to check its updatedAt
    const folder = allFolders.find((f) => f.id === id);
    if (folder) {
      latestUpdate = Math.max(latestUpdate, folderUpdatedSortMs(folder));
    }

    // Check all recipes in this folder
    allRecipes
      .filter((r) => r.folderId === id)
      .forEach((recipe) => {
        latestUpdate = Math.max(latestUpdate, recipeUpdatedSortMs(recipe));
      });

    // Check all subfolders recursively
    allFolders
      .filter((f) => f.parentId === id)
      .forEach((subfolder) => {
        latestUpdate = Math.max(latestUpdate, calculateLatestUpdate(subfolder.id));
      });

    // Cache the result
    cache.set(id, latestUpdate);
    return latestUpdate;
  };

  return calculateLatestUpdate(folderId);
};

/**
 * Create a memoized version of getLatestUpdateInFolder
 */
export const createGetLatestUpdateInFolder = (allFolders: FolderResponseDto[], allRecipes: DashboardRecipe[]) => {
  const cache = new Map<string, number>();

  return (folderId: string): number => {
    if (cache.has(folderId)) {
      return cache.get(folderId)!;
    }

    const result = getLatestUpdateInFolder(folderId, allFolders, allRecipes);
    cache.set(folderId, result);
    return result;
  };
};
